import { useMemo, useState } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../../lib/stripe';
import CalculatorLayout from '../../../components/calculator/CalculatorLayout';
import NumberInput from '../../../components/calculator/NumberInput';
import PercentInput from '../../../components/calculator/PercentInput';
import BigResult from '../../../components/calculator/BigResult';
import EmailCapture from '../../../components/calculator/EmailCapture';
import CtaBlock from '../../../components/calculator/CtaBlock';
import FaqSection from '../../../components/calculator/FaqSection';
import RelatedCalculators from '../../../components/calculator/RelatedCalculators';

const SLUG = 'lead-value';
const CANONICAL = `https://floridaaimarketingexperts.com/calculator/${SLUG}`;

export default function LeadValueCalculatorPage() {
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Lead Value Calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free lead value calculator. See the true dollar value of one qualified lead using close rate and lifetime value multiplier.',
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is the average lead value for a Florida business?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'It depends on the vertical. A roofing lead is often $800-$2,000 because of high tickets and strong referrals. A B2B SaaS lead can be $1,500+. Use the lifetime multiplier to account for repeat work and referrals.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I calculate lead value?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Multiply your average deal value by your close rate percentage. Then multiply by a lifetime value multiplier (usually 1.5x to 3x) to account for repeat business and referrals. That is what one qualified lead is actually worth.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why do I need to know my lead value?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'So you know what you can pay to acquire one. If your lead value is $600, spending $50 to get one is a 12x return. Knowing lead value turns marketing from a gamble into math — and is the #1 input for retainer ROI conversations.',
        },
      },
    ],
  };

  usePageTitle({
    title: 'Lead Value Calculator | FAME',
    description:
      'Free lead value calculator for Florida businesses. See what one qualified lead is actually worth — close rate, lifetime value, and referral math included.',
    keywords:
      'lead value calculator, cost per lead calculator, lead worth calculator, Florida business lead value',
    canonical: CANONICAL,
    schema: [softwareSchema, faqSchema],
  });

  const [avgJobValue, setAvgJobValue] = useState(3500);
  const [closeRate, setCloseRate] = useState(35);
  const [ltvMultiplier, setLtvMultiplier] = useState(1.8);

  const results = useMemo(() => {
    const immediateValue = avgJobValue * (closeRate / 100);
    const trueLeadValue = immediateValue * ltvMultiplier;
    const maxCpl = trueLeadValue / 3;
    return { immediateValue, trueLeadValue, maxCpl };
  }, [avgJobValue, closeRate, ltvMultiplier]);

  const faqs = [
    {
      question: 'What counts as a "qualified" lead?',
      answer:
        'A qualified lead has a real problem in your service area, has budget, and has given you enough info to quote or book. A web form fill with a working phone number, service, and ZIP code is qualified. A random email with no context is not.',
    },
    {
      question: 'Why does the lifetime value multiplier matter?',
      answer:
        'Because one happy customer becomes three more over time — they call you back and they refer neighbors. The industry average for service businesses is 1.5x-3x lifetime multiplier. Roofers, remodelers, and professional services skew higher.',
    },
    {
      question: 'Should I pay more than my lead value?',
      answer:
        'Never. A healthy business spends 15-35% of lead value on acquisition. If you are paying $80 for leads worth $600, you are in great shape. If you are paying $400 for leads worth $500, one bad month breaks the business.',
    },
  ];

  return (
    <CalculatorLayout>
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="eyebrow mb-5">
            <i className="ri-money-dollar-circle-line mr-2"></i>Free Calculator · Know Your Numbers
          </span>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[0.95] tracking-[-0.02em]">
            Lead Value Calculator
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl mx-auto">
            The real dollar value of one qualified lead — accounting for close rate, repeat work,
            and referrals. Stop guessing what you can pay for leads.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="fame-card p-8 shadow-xl">
              <h2 className="text-navy-900 text-2xl font-display font-bold mb-6">Your Numbers</h2>
              <div className="flex flex-col gap-6">
                <NumberInput
                  label="Average deal value"
                  value={avgJobValue}
                  onChange={setAvgJobValue}
                  prefix="$"
                  step={100}
                  helperText="Your typical booked deal."
                />
                <PercentInput
                  label="Close rate on qualified leads"
                  value={closeRate}
                  onChange={setCloseRate}
                  helperText="Most Florida businesses sit at 25-50%."
                />
                <NumberInput
                  label="Lifetime value multiplier"
                  value={ltvMultiplier}
                  onChange={setLtvMultiplier}
                  min={1}
                  max={10}
                  step={0.1}
                  suffix="x"
                  helperText="Repeat work + referrals. 1.5x-3x is typical."
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <BigResult
                label="True Value of One Qualified Lead"
                value={results.trueLeadValue}
                format="currency"
                accent="green"
                sublabel="Includes repeat work and referral multiplier."
              />
              <BigResult
                label="Immediate Revenue Value"
                value={results.immediateValue}
                format="currency"
                accent="teal"
                size="md"
                sublabel="Deal value × close rate — first deal only."
              />
              <BigResult
                label="Max You Should Pay Per Lead"
                value={results.maxCpl}
                format="currency"
                accent="orange"
                size="md"
                sublabel="Keeps you at a healthy 3:1 return."
              />

              <CtaBlock
                variant="compact"
                eyebrow="Predictable lead flow"
                headline="Starter Retainer · $2,500/mo"
                subheadline="One team owns ads + SEO + voice + attribution. Single-pillar focus."
                primaryLabel="Start Retainer"
                primaryHref={STRIPE_LINKS.T10_STARTER}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4">
            Why most Florida businesses under-price their own leads
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            Ask ten business owners what a lead is worth and you will get ten wild guesses. That is
            the entire problem. If you do not know your lead value, you cannot make rational
            decisions about marketing spend, and you default to whatever a lead vendor charges —
            which is designed to extract your margin, not protect it.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            True lead value is three multipliers deep:{' '}
            <strong>deal value × close rate × lifetime multiplier</strong>. A $3,500 deal at a 35%
            close rate and 1.8x lifetime gives a true lead value of <strong>$2,205</strong>. That
            single number changes how you run the business. You can pay $50 per lead with
            confidence. You can hire full-time demand generation. You can walk away from any vendor
            charging more than $700.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed">
            FAME&apos;s T10 Starter Retainer is built around this math. We feed your pipeline with
            integrated SEO, paid media, generative visibility, and booking automation that produces
            leads at a predictable cost below 20% of their true value — which is how you compound
            instead of treading water.
          </p>
        </div>
      </section>

      <FaqSection items={faqs} />

      <CtaBlock
        eyebrow="Stop guessing, start compounding"
        headline="Get leads below 20% of their true value"
        subheadline="FAME T10 Starter Retainer — SEO, ads, voice AI, attribution. $2,500/mo, month-to-month."
        primaryLabel="Start Retainer · $2,500/mo"
        primaryHref={STRIPE_LINKS.T10_STARTER}
        secondaryLabel="Scale Up · Growth $5,000/mo"
        secondaryHref={STRIPE_LINKS.T10_GROWTH}
      />

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <EmailCapture
            slug={SLUG}
            calculatorInputs={{ avgJobValue, closeRate, ltvMultiplier }}
            calculatorResults={{
              trueLeadValue: Math.round(results.trueLeadValue),
              immediateValue: Math.round(results.immediateValue),
              maxCpl: Math.round(results.maxCpl),
            }}
          />
        </div>
      </section>

      <RelatedCalculators excludeSlug={SLUG} />
    </CalculatorLayout>
  );
}
